import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Box, Typography, TextField, Button, Paper } from '@mui/material'
import { useSnackbar } from 'notistack'
import { apiClient } from '@/services/apiClient'
import { PrayerRequest } from '@/types'
import MediaUploader, { UploadedMedia } from '@/components/MediaUploader'

export default function CreatePrayerRequest() {
  const { groupId } = useParams<{ groupId: string }>()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [mediaUploads, setMediaUploads] = useState<UploadedMedia[]>([])
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim() || !description.trim() || !groupId) return
    setSubmitting(true)
    try {
      const pr = await apiClient.post<PrayerRequest>(`/api/prayerrequests/group/${groupId}`, {
        title: title.trim(),
        description: description.trim(),
        mediaIds: mediaUploads.map(u => u.id),
      })
      enqueueSnackbar('Prayer request created', { variant: 'success' })
      navigate(`/prayer-requests/${pr.id}`)
    } catch {
      enqueueSnackbar('Failed to create prayer request', { variant: 'error' })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto' }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
          New Prayer Request
        </Typography>
        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Title"
            value={title}
            onChange={e => setTitle(e.target.value)}
            margin="normal"
            required
            autoFocus
            slotProps={{ htmlInput: { maxLength: 200 } }}
          />
          <TextField
            fullWidth
            label="Description"
            value={description}
            onChange={e => setDescription(e.target.value)}
            margin="normal"
            required
            multiline
            rows={4}
            slotProps={{ htmlInput: { maxLength: 5000 } }}
          />
          <Box sx={{ mt: 1 }}>
            <MediaUploader
              uploads={mediaUploads}
              onChange={setMediaUploads}
              disabled={submitting}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button onClick={() => navigate(-1)} sx={{ flex: 1 }}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={submitting || !title.trim() || !description.trim()}
              sx={{ flex: 1 }}
            >
              {submitting ? 'Creating...' : 'Create'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  )
}
